import './style.modules.scss'

import React, { useEffect, useRef, useState } from 'react'

export type StickyRightRailProps = {
  children: React.ReactNode
  className?: string
  testId?: string
}

export const StickyRightRail: React.FC<StickyRightRailProps> = ({
  testId,
  className,
  children
}) => {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const childRef = useRef<HTMLDivElement | null>(null)
  const [parentRect, setParentRect] = useState<DOMRect | null>(null)
  const [childRect, setChildRect] = useState<DOMRect | null>(null)
  const [prevScrollY, setPrevScrollY] = useState<number>(0)
  const [isShyHeaderShown, setIsShyHeaderShown] = useState<boolean>(false)

  useEffect(() => {
    const parent = parentRef.current
    const child = childRef.current

    if (!parent || !child) return

    const updateOffsets = () => {
      const currentScrollY = window.scrollY
      const menuElement = (() => {
        const newMenu = document.querySelector('.AppMenu--sticky')
        if (newMenu) return newMenu

        const ameripriseMenu = document.querySelector('ameriprise-menu')
        if (ameripriseMenu?.shadowRoot) {
          return ameripriseMenu.shadowRoot.querySelector('.amp-nav.sticky')
        }

        return null
      })()

      // Check if the AppMenu--sticky element is present
      if (menuElement) {
        setIsShyHeaderShown(true)
      } else {
        setIsShyHeaderShown(false)
      }

      const newParentRect = parent.getBoundingClientRect()
      const newChildRect = child.getBoundingClientRect()
      setParentRect(newParentRect)
      setChildRect(newChildRect)
      setPrevScrollY(currentScrollY)
    }

    updateOffsets()

    window.addEventListener('scroll', updateOffsets)
    window.addEventListener('resize', updateOffsets)

    return () => {
      window.removeEventListener('scroll', updateOffsets)
      window.removeEventListener('resize', updateOffsets)
    }
  }, [])

  useEffect(() => {
    if (!parentRect || !childRect) return

    const baseTopOffset = 30
    const scrollY = window.scrollY
    const topOffset = isShyHeaderShown ? 82 : baseTopOffset
    const child = childRef.current
    const parent = parentRef.current

    if (!child || !parent) return

    // disable sticky behavior for viewports smaller than or equal to 1024px
    if (window.innerWidth <= 1024) {
      child.style.position = 'relative'
      child.style.top = '0'
      child.style.width = 'auto'
      child.style.bottom = 'auto'
      return
    }

    // set the child to absolute positioning if its bottom exceeds the parent's bottom boundary
    if (childRect.height > parentRect.bottom) {
      child.style.position = 'absolute'
      child.style.top = 'auto'
      child.style.bottom = '0'
      child.style.width = 'auto'
      return
    }

    // set the child to fixed positioning if the scroll position is past the parent's top and the parent is within the top offset
    if (scrollY > parentRect.y && parentRect.y <= topOffset) {
      child.style.position = 'fixed'
      child.style.top = `${topOffset}px`
      child.style.bottom = 'auto'
      child.style.width = `${childRect.width}px`
      return
    }

    // set the child to fixed positioning when scrolling up and the child is above the top offset but still within the parent's top boundary
    if (
      scrollY < prevScrollY &&
      childRect.top > topOffset &&
      childRect.top > parentRect.top
    ) {
      child.style.position = 'fixed'
      child.style.top = `${topOffset}px`
      child.style.bottom = 'auto'
      child.style.width = `${childRect.width}px`
      return
    }

    // reset the child to relative positioning if none of the above conditions are met
    child.style.position = 'relative'
    child.style.top = '0'
    child.style.width = 'auto'
    child.style.bottom = 'auto'
  }, [prevScrollY, parentRect, childRect, isShyHeaderShown])

  return (
    <div
      className={`StickyRightRail ${className ?? ''}`}
      ref={parentRef}
      data-testid={testId ?? null}
    >
      <div
        className="StickyRightRail-content u-marginBottom4 u-sm-paddingHoriz8 u-md-paddingHoriz8"
        ref={childRef}
        data-testid={testId ? `${testId}-content` : null}
      >
        {children}
      </div>
    </div>
  )
}
